import type { AvailableSession, BusinessSession, MyBusiness, TimetableEditor } from "@teamna/shared";
import type { UseQueryResult } from "@tanstack/react-query";
import { track } from "@teamna/web-client/analytics/web.ts";
import { TakeAttendanceScreen } from "../../attendance/take-attendance-screen.tsx";
import { CalendarIcon, EmptyState, ErrorState, SkeletonList } from "../../components/index.ts";
import { SessionRail, useScheduleWords } from "../schedule-grid.tsx";
import { canTakeAttendance, todayStatusText, type TodayProgress } from "./progress.ts";
import { TodayStale } from "./TodayStale.tsx";

/**
 * One session in a panel: the rail of the Week view (`SessionRail`: who is coming, the coach, move,
 * cancel) with Take attendance kept at the bottom, and Take attendance swaps in the same
 * `TakeAttendanceScreen` the coaches use; its back link returns to the session.
 *
 * The Calendar's Day view shows it in its right column, and owner Home in a side panel over the page
 * (`SessionPanel` below). Home opened the Calendar before (2026-10-22): on a phone, Back then landed
 * on the Calendar, and the way back to Home was not clear.
 */
export function SessionPanelContent({
  business,
  open,
  owned,
  editor,
  attending,
  onAttendingChange,
  attendanceSource,
  onClose,
}: {
  business: MyBusiness;
  open: { session: BusinessSession; progress: TodayProgress };
  /** The same sessions as the Week view reads them, for the rail. */
  owned: UseQueryResult<AvailableSession[]>;
  editor: TimetableEditor | undefined;
  /** Take attendance is on screen in place of the rail. */
  attending: boolean;
  onAttendingChange: (attending: boolean) => void;
  /** Take attendance was opened here: `attendance_opened`'s source. */
  attendanceSource: "home_today" | "calendar_today" | "calendar_day";
  onClose: () => void;
}) {
  const timezone = business.timezone || "Asia/Dubai";
  const words = useScheduleWords();
  const ownedOpen = owned.data?.find((row) => row.id === open.session.id);

  if (attending && canTakeAttendance(open.progress)) {
    return (
      <TakeAttendanceScreen
        key={open.session.id}
        business={business}
        businessLoading={false}
        sessionId={open.session.id}
        exitLabel="Session"
        fillScreen={false}
        onExit={() => onAttendingChange(false)}
      />
    );
  }
  if (ownedOpen) {
    return (
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-3 p-3.5">
        {owned.isError ? <TodayStale onRetry={() => void owned.refetch()} /> : null}
        <SessionRail
          key={open.session.id}
          session={ownedOpen}
          level={editor?.groups.find((level) => level.id === ownedOpen.groupId)}
          team={editor?.team ?? []}
          businessId={business.businessId}
          timezone={timezone}
          words={words}
          stickyAttendance
          onClose={onClose}
          onTakeAttendance={() => {
            onAttendingChange(true);
            track("attendance_opened", { source: attendanceSource });
          }}
        />
      </div>
    );
  }
  if (owned.isLoading) {
    return (
      <div className="p-4">
        <SkeletonList rows={3} />
      </div>
    );
  }
  if (owned.error) {
    return (
      <div className="p-4">
        <ErrorState error={owned.error} onRetry={() => void owned.refetch()} />
      </div>
    );
  }
  return (
    <div className="p-5">
      <EmptyState
        icon={<CalendarIcon size={28} />}
        title={`${open.session.groupName}: ${todayStatusText(open.progress, open.session, timezone)}`}
        message="There is no attendance to take for this session."
      />
    </div>
  );
}
